<template>
  <div class="promocode-wrapper d-flex fl-col fl-j-c fl-a-c">
    <div class="inputs-wrapper" style="padding-top: 10%">
      <div class="mx-auto flex w-[96%] gap-2.5">
        <SInputGroup class="h-20 min-w-0 flex-1">
          <SInputGroupInput
            class="h-full text-[26px]"
            :placeholder="t('promocode.enter')"
            :model-value="voucherCode"
            @update:model-value="(v) => (voucherCode = String(v))"
          />
          <SInputGroupAddon align="inline-end">
            <SvgIcon
              :iconContent="editPenIconContent"
              :iconAlt="editPenIconAlt"
              :iconStyle="editPenIconStyle"
            />
          </SInputGroupAddon>
        </SInputGroup>
        <SButton
          type="button"
          class="h-20 w-[30%] shrink-0 text-3xl"
          :loading="isLoading"
          :disabled="!isPromocodeLengthValid"
          @click="applyIncertVoucher"
        >
          {{ $t("promocode.apply") }}
        </SButton>
      </div>
    </div>

    <div
      style="
        width: 80%;
        margin: auto;
        height: 80%;
        display: flex;
        flex-direction: row;
      "
    >
      <div
        style="letter-spacing: 1px; margin: 20px"
        v-for="voucherInfo in appliedVoucherInfos"
        :key="voucherInfo.voucherCode"
      >
        <p style="margin-bottom: 4px">Code: {{ voucherInfo.voucherCode }}</p>
        <p style="margin-bottom: 4px">Status: Valid</p>
        <p
          v-if="voucherInfo.remainingValue !== undefined"
          style="margin-bottom: 4px"
        >
          Remaining Value:
          {{
            voucherInfo.partiallyRedeemable === false
              ? (0).toFixed(2)
              : Number(voucherInfo.remainingValue).toFixed(2)
          }}{{ voucherInfo.currency }}
        </p>
        <p
          v-if="voucherInfo.partiallyRedeemable !== undefined"
          style="margin-bottom: 4px"
        >
          Partially redeemable:
          {{ voucherInfo.partiallyRedeemable ? "Yes" : "No" }}
        </p>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useOrderStore } from "@smeetz/order-store";
import NotificationService from "@/services/notification.service";
import { LogLevel, LogService } from "@/services/logging";
import editPen from "@/assets/images/svg/edit-pen-rectangle.svg";
import { Currencies } from "@/constants/currency";
import ScannerModule from "@/store/modules/scanner";
import { extractCodeFromUrl } from "@/utils/vue-helpers/promocode";
import { applyDiscountCode } from "@/utils/vue-helpers/orders.helper";
import {
  SButton,
  SInputGroup,
  SInputGroupAddon,
  SInputGroupInput,
} from "@smeetz/ui-core";

export default defineComponent({
  name: "IncertVoucher",
  components: {
    SButton,
    SInputGroup,
    SInputGroupAddon,
    SInputGroupInput,
  },
  setup() {
    const { t } = useI18n({});
    const editPenIconStyle = "width: 30px; height: 30px";
    const editPenIconContent = editPen;
    const editPenIconAlt = "Edit";
    const voucherCode = ref("");
    const isLoading = ref(false);

    const orderStore = useOrderStore();

    const isPromocodeLengthValid = computed(() => {
      return voucherCode.value.length >= 5;
    });

    const currency = computed(() => {
      return orderStore.selectedOrder?.orderSession?.currency || Currencies.CHF;
    });

    const appliedVoucherInfos = computed(() => {
      const vouchers = orderStore.selectedOrderSummary.vouchers ?? [];
      const sessionCurrency =
        orderStore.selectedOrderSummary.currency || Currencies.CHF;

      return vouchers.map((voucher) => {
        const applied = Number(voucher.value || 0);
        // `Voucher.remainingValue` is the at-apply-time balance; subtract
        // `value` to display the post-apply remaining.
        const remainingValue =
          voucher.remainingValue !== undefined &&
          voucher.remainingValue !== null
            ? Number(voucher.remainingValue) - applied
            : undefined;
        return {
          voucherCode: String(voucher.code ?? ""),
          appliedValue: applied,
          currency: voucher.currency || sessionCurrency,
          remainingValue,
          partiallyRedeemable: voucher.partiallyRedeemable,
        };
      });
    });

    const scanPromocodeModal = ref(false);
    const ScannerTog = computed(() => {
      return ScannerModule.tog;
    });

    watch(
      ScannerTog,
      async () => {
        if (ScannerModule.value && scanPromocodeModal.value) {
          LogService.info(
            `Incert Voucher: scanner triggered, value = ${ScannerModule.value}`
          );
          // The scanned valued can be a json object (Locarno logic) and can be a url (old logic for everyone else)

          // Check if the voucherCode is a URL
          const scannedValue = ScannerModule.value;
          const isUrl =
            String(scannedValue).startsWith("http://") ||
            String(scannedValue).startsWith("https://") ||
            String(scannedValue).startsWith("http//") ||
            String(scannedValue).startsWith("https//");

          if (isUrl) {
            LogService.info(`Incert Voucher: scanned value is in url format`);
            // We keep the old logic of extracting the code from the url scanned
            const code = extractCodeFromUrl(ScannerModule.value);
            LogService.info(
              `Incert Voucher: extracted from qrcode string code: ${code}`
            );
            if (code && code.length >= 5) {
              voucherCode.value = code;
              LogService.info(
                `Incert Voucher: going to process scanned voucher code: ${voucherCode.value}`
              );
              await applyIncertVoucher();
              LogService.info(
                `Incert Voucher: Done processing scanned voucher code: ${voucherCode.value}`
              );
              scanPromocodeModal.value = false;
            }
          } else {
            LogService.info(
              `Incert Voucher: scanned value is not in url format. Checking if it is json object.`
            );
            // Check if the scanned value is actually a json object
            try {
              const scannedJsonCode = JSON.parse(scannedValue);
              LogService.info(
                `Incert Voucher: scanned value is in json format attached.`,
                { params: scannedJsonCode }
              );
              // Check if it's in the expected format
              if (
                scannedJsonCode &&
                scannedJsonCode.code &&
                scannedJsonCode.token
              ) {
                LogService.info(
                  `Incert Voucher: scanned value is exactly in the expected format.`,
                  { params: scannedJsonCode }
                );
                // Lets proceed applying the code
                voucherCode.value = scannedJsonCode.code;
                LogService.info(
                  `Incert Voucher: going to process scanned voucher code: ${voucherCode.value}`
                );
                await applyIncertVoucher();
                LogService.info(
                  `Incert Voucher: Done processing scanned voucher code: ${voucherCode.value}`
                );
                scanPromocodeModal.value = false;
              } else {
                LogService.info(
                  `Incert Voucher: scanned value is not in the expected format.`,
                  { params: scannedJsonCode }
                );
                // Lets notify the user that code couldn't be scanned
                NotificationService.showCustomMessage(
                  t("promocode.could-not-scan"),
                  "error"
                );
              }
            } catch (err) {
              // Couldn't parse the scanned value into a json object, this means the scanned value isn't a url and isn't a json obj, lets log
              LogService.info(
                `Incert Voucher Error: scanned value is neither a url nor a valid json object: ${voucherCode.value}`,
                { param: { scannedValue, err } },
                LogLevel.Error
              );
              // Lets notify the user that code couldn't be scanned
              NotificationService.showCustomMessage(
                t("promocode.could-not-scan"),
                "error"
              );
            }
          }
        }
      },
      { deep: true }
    );

    /**
     * Applies the current `voucherCode` to the active order. Routing between
     * promo / gift-card / voucher / membership is handled inside the discount
     * package via the registered `POSExternalVoucherProvider`.
     */
    async function applyIncertVoucher() {
      isLoading.value = true;
      try {
        await applyDiscountCode(String(voucherCode.value));
      } catch {
        // surfaced by applyDiscountCode
      } finally {
        isLoading.value = false;
      }
    }

    return {
      appliedVoucherInfos,
      voucherCode,
      isLoading,
      editPenIconStyle,
      editPenIconContent,
      editPenIconAlt,
      isPromocodeLengthValid,
      t,
      scanPromocodeModal,
      currency,
      applyIncertVoucher,
    };
  },
  methods: {
    openScanPromocodeModal() {
      this.scanPromocodeModal = true;
    },
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;

.promocode-wrapper {
  // padding-top: 80px;
  .btn-height {
    height: 50px;
  }

  .inputs-wrapper {
    width: 80%;
  }

  .promocode-discount-result-wrapper {
    margin-top: 50px;
    width: 80%;

    .success-text {
      width: 90%;
      margin-left: auto;
      margin-right: auto;
      margin-bottom: 20px;
      color: #008000;
      font-weight: 600;
    }

    .discount-text {
      width: 90%;
      margin-left: auto;
      margin-right: auto;
      height: 110px;
      border-radius: 6px;
      background-color: #e5e5e5;
      font-size: 40px;
      color: $color-dark-grey;
    }
  }

  .options-wrapper {
    padding-bottom: 30px;

    .btn-option {
      justify-content: start;
      flex-grow: 1;
      text-align: left;
      border-radius: 9px;
      background-color: #e5e5e5;
      color: #000;
      border: 0;
    }

    .selected {
      background-color: #6aa6b7;
      color: #fff;
    }
  }
}

.remaining-amount {
  margin-left: 20px;
}

.remaining-amount-text {
  font-size: 0.9rem;
  font-weight: bolder;
  background-color: $color-background-green;
  border-radius: 5px;
  padding: 3px 15px;
}

.scan-promocode-modal .content {
  padding-bottom: 30px;
  font-size: 18px;
  font-weight: 700;
}
</style>
